import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { ticketService, customerService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Trash2, Search, Loader2, AlertTriangle } from "lucide-react";
import { PRIORITIES, STATUSES, STATUS_COLOR, PRIORITY_COLOR } from "./constants";

const EMPTY = { subject: "", description: "", priority: "Medium", category: "General", customerId: "", tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function TicketsPage() {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await ticketService.list({
        q: q || undefined, status: statusFilter || undefined, priority: priorityFilter || undefined, size: 100,
      });
      setRows(d.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, statusFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const loadCustomers = (tenantId) => {
    customerService.list({ tenantId: tenantId || undefined, size: 200 })
      .then((d) => setCustomers(d.items || [])).catch(() => {});
  };

  const openCreate = () => { setForm(EMPTY); setOpen(true); if (!isSuper) loadCustomers(); };

  const save = async () => {
    if (!form.subject.trim()) return toast.error("Subject is required");
    if (!form.customerId) return toast.error("Select a customer");
    if (isSuper && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isSuper) delete payload.tenantId;
      await ticketService.create(payload);
      toast.success("Ticket created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ticket ${row.ticketNumber}?`)) return;
    try { await ticketService.remove(row.id); toast.success("Ticket deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("TICKET.CREATE");
  const canDelete = hasPermission("TICKET.DELETE");

  const columns = [
    { key: "ticketNumber", header: "Ticket", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.ticketNumber}</span> },
    { key: "subject", header: "Subject", render: (r) => <span className="font-medium text-foreground">{r.subject}</span> },
    { key: "customerName", header: "Customer", render: (r) => r.customerName || "—" },
    { key: "priority", header: "Priority", render: (r) => <Badge color={PRIORITY_COLOR[r.priority] || "slate"}>{r.priority}</Badge> },
    { key: "status", header: "Status", render: (r) => <Badge color={STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge> },
    {
      key: "sla", header: "SLA",
      render: (r) => {
        const s = r.slaState || {};
        if (s.resolutionOverdue || s.responseOverdue) return <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold" data-testid={`ticket-sla-breached-${r.id}`}><AlertTriangle className="h-3.5 w-3.5" /> Breached</span>;
        if (s.resolutionMinsRemaining != null) {
          const h = Math.floor(s.resolutionMinsRemaining / 60);
          return <span className="text-xs text-muted-foreground">{h > 0 ? `${h}h left` : `${s.resolutionMinsRemaining}m left`}</span>;
        }
        return <span className="text-xs text-muted-foreground">—</span>;
      },
    },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => navigate(`/admin/tickets/${r.id}`)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`ticket-view-${r.id}`}><Eye className="h-4 w-4" /></button>
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`ticket-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="tickets-page">
      <PageHeader title="Tickets" subtitle="Customer service requests with SLA tracking and escalation"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="ticket-create-btn">
            <Plus className="h-4 w-4" /> New Ticket
          </button>
        )}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 w-64">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, ticket #…" className="bg-transparent text-sm outline-none w-full text-foreground" data-testid="ticket-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="ticket-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="ticket-priority-filter">
          <option value="">All priorities</option>
          {PRIORITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <DataTable columns={columns} rows={rows} loading={loading} testid="tickets-table" empty="No tickets yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ticket-dialog">
            <h3 className="text-lg font-bold mb-4">New Ticket</h3>
            <div className="space-y-3.5">
              {isSuper && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => { setForm({ ...form, tenantId: e.target.value, customerId: "" }); loadCustomers(e.target.value); }} className="ciq-input" data-testid="ticket-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Customer *">
                <select value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })} className="ciq-input" data-testid="ticket-customer">
                  <option value="">{customers.length ? "Select customer…" : "Select tenant first"}</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.customerName}</option>)}
                </select>
              </Field>
              <Field label="Subject *"><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="ciq-input" data-testid="ticket-subject" /></Field>
              <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" rows={3} data-testid="ticket-description" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Priority"><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="ciq-input" data-testid="ticket-priority">{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></Field>
                <Field label="Category"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="ciq-input" /></Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted" data-testid="ticket-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="ticket-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
